/**
 * Get pagination total count from response headers
 * @param {Headers} headers - Response headers object
 * @returns {number} Total count from pagination header
 */
export const getPaginationTotalCount = (headers) => {
  if (!headers) {
    console.log('No headers available for pagination count');
    return 0;
  }
  
  // Check multiple possible header names
  const possibleHeaders = [
    'x-pagination-total-count',
    'x-total-count',
    'total-count',
    'x-pagination-page-count',
    'x-page-count',
    'x-total-items',
    'content-range' // Sometimes used for pagination
  ];
  
  for (const headerName of possibleHeaders) {
    try {
      let headerValue;
      
      // Try different ways to access headers (production vs development)
      if (typeof headers.get === 'function') {
        headerValue = headers.get(headerName);
      } else if (typeof headers[headerName] !== 'undefined') {
        headerValue = headers[headerName];
      } else if (headers._headers && headers._headers[headerName]) {
        headerValue = headers._headers[headerName];
      } else if (headers.map && typeof headers.map.get === 'function') {
        headerValue = headers.map.get(headerName);
      }
      
      if (headerValue) {
        // Handle array values (some implementations return arrays)
        if (Array.isArray(headerValue)) {
          headerValue = headerValue[0];
        }
        
        // Handle content-range header format: "items 0-9/100"
        if (headerName === 'content-range') {
          const match = headerValue.match(/\/(\d+)$/);
          if (match) {
            const totalCount = parseInt(match[1], 10);
            if (!isNaN(totalCount) && totalCount > 0) {
              console.log(`Found total count ${totalCount} from ${headerName} header`);
              return totalCount;
            }
          }
        } else {
          const totalCount = parseInt(headerValue, 10);
          if (!isNaN(totalCount) && totalCount > 0) {
            console.log(`Found total count ${totalCount} from ${headerName} header`);
            return totalCount;
          }
        }
      }
    } catch (error) {
      console.warn(`Error reading header ${headerName}:`, error);
    }
  }
  
  console.log('No pagination count found in headers');
  return 0;
};

/**
 * Calculate total pages based on total items and items per page
 * @param {number} totalItems - Total number of items
 * @param {number} itemsPerPage - Items per page
 * @returns {number} Total number of pages
 */
export const calculateTotalPages = (totalItems, itemsPerPage) => {
  if (!totalItems || !itemsPerPage || totalItems <= 0 || itemsPerPage <= 0) {
    return 1;
  }
  return Math.ceil(totalItems / itemsPerPage);
};

/**
 * Get pagination info for display
 * @param {number} currentPage - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {number} totalItems - Total number of items
 * @param {number} itemsPerPage - Items per page
 * @returns {object} Pagination display info
 */
export const getPaginationInfo = (currentPage, totalPages, totalItems, itemsPerPage) => {
  const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  
  return {
    startItem,
    endItem,
    totalItems,
    currentPage,
    totalPages,
    hasNext: currentPage < totalPages,
    hasPrevious: currentPage > 1
  };
};